import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Server, Shield, Zap, Lock, Globe, Building2,
  Cpu, Brain, CheckCircle, ArrowRight, Database,
  Cloud, Network, Users, HeartPulse, Clock,
  MapPin, Flag, Sparkles, Bot
} from 'lucide-react';
import { usePageTitle } from '../../hooks/usePageTitle';
import { useI18n } from '../../i18n';

const DedicatedServer = () => {
  const { t } = useI18n();
  usePageTitle(t('dedicatedServer.pageTitle'));
  const navigate = useNavigate();

  const advantages = [
    {
      icon: Shield,
      title: t('dedicatedServer.adv1Title'),
      description: t('dedicatedServer.adv1Desc')
    },
    {
      icon: Zap,
      title: t('dedicatedServer.adv2Title'),
      description: t('dedicatedServer.adv2Desc')
    },
    {
      icon: Lock,
      title: t('dedicatedServer.adv3Title'),
      description: t('dedicatedServer.adv3Desc')
    },
    {
      icon: HeartPulse,
      title: t('dedicatedServer.adv4Title'),
      description: t('dedicatedServer.adv4Desc')
    },
    {
      icon: Users,
      title: t('dedicatedServer.adv5Title'),
      description: t('dedicatedServer.adv5Desc')
    },
    {
      icon: Clock,
      title: t('dedicatedServer.adv6Title'),
      description: t('dedicatedServer.adv6Desc')
    }
  ];

  const hostingOptions = [
    {
      icon: Building2,
      title: t('dedicatedServer.hostingDatacenterTitle'),
      location: t('dedicatedServer.hostingDatacenterLoc'),
      features: [
        'Certification Tier III',
        'Alimentation électrique redondante',
        'Refroidissement haute efficacité',
        'Sécurité physique 24/7',
        'Connexion fibre 10 Gbps',
        'Conformité RGPD native'
      ],
      highlight: true
    },
    {
      icon: Network,
      title: t('dedicatedServer.hostingOnPremTitle'),
      location: t('dedicatedServer.hostingOnPremLoc'),
      features: [
        'Installation dans votre réseau',
        'Contrôle total des données',
        'Intégration SIEM/SOC',
        'Accès réseau interne direct',
        'Personnalisation complète',
        'Formation des équipes'
      ],
      highlight: false
    }
  ];

  const aiModels = [
    {
      name: 'SYLink IA 8B',
      params: '8 milliards',
      description: t('dedicatedServer.ai8bDesc'),
      specs: t('dedicatedServer.ai8bSpecs'),
      useCase: t('dedicatedServer.ai8bUseCase'),
      color: 'from-blue-500 to-cyan-500'
    },
    {
      name: 'SYLink IA 30B',
      params: '30 milliards',
      description: t('dedicatedServer.ai30bDesc'),
      specs: t('dedicatedServer.ai30bSpecs'),
      useCase: t('dedicatedServer.ai30bUseCase'),
      color: 'from-purple-500 to-pink-500',
      recommended: true
    },
    {
      name: 'SYLink IA 80B',
      params: '80 milliards',
      description: t('dedicatedServer.ai80bDesc'),
      specs: t('dedicatedServer.ai80bSpecs'),
      useCase: t('dedicatedServer.ai80bUseCase'),
      color: 'from-orange-500 to-red-500'
    }
  ];

  return (
    <div className="min-h-screen bg-brand-black">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-brand-green/10 via-transparent to-purple-500/10" />
        <div className="absolute inset-0">
          <div className="absolute top-20 left-20 w-72 h-72 bg-brand-green/20 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 py-20">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-brand-green/10 border border-brand-green/30 rounded-full text-brand-green text-sm mb-6">
              <Flag className="w-4 h-4" />
              {t('dedicatedServer.heroTag')}
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
              {t('dedicatedServer.heroTitle')} <span className="text-brand-green">{t('dedicatedServer.heroTitleHighlight')}</span>
            </h1>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              {t('dedicatedServer.heroDesc')}
            </p>
          </div>

          {/* Key Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { value: '100%', label: t('dedicatedServer.statFrench'), icon: Flag },
              { value: 'Aucune', label: t('dedicatedServer.statQueue'), icon: Zap },
              { value: '24/7', label: t('dedicatedServer.statSupervision'), icon: HeartPulse },
              { value: '99.9%', label: t('dedicatedServer.statAvailability'), icon: CheckCircle }
            ].map((stat, idx) => (
              <div key={idx} className="bg-brand-darker/50 backdrop-blur border border-gray-800 rounded-xl p-6 text-center">
                <stat.icon className="w-8 h-8 text-brand-green mx-auto mb-3" />
                <div className="text-3xl font-bold text-white mb-1">{stat.value}</div>
                <div className="text-sm text-gray-400">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Advantages Section */}
      <div className="max-w-7xl mx-auto px-6 py-16">
        <h2 className="text-3xl font-bold text-white text-center mb-4">
          {t('dedicatedServer.whyDedicated')}
        </h2>
        <p className="text-gray-400 text-center max-w-2xl mx-auto mb-12">
          {t('dedicatedServer.whyDedicatedDesc')}
        </p>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {advantages.map((advantage, idx) => (
            <div
              key={idx}
              className="bg-brand-darker border border-gray-800 rounded-xl p-6 hover:border-brand-green/50 transition-all group"
            >
              <div className="w-12 h-12 bg-brand-green/10 rounded-lg flex items-center justify-center mb-4 group-hover:bg-brand-green/20 transition-colors">
                <advantage.icon className="w-6 h-6 text-brand-green" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">{advantage.title}</h3>
              <p className="text-gray-400 text-sm break-words">{advantage.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Hosting Options */}
      <div className="bg-brand-darker/50 py-16">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-white text-center mb-4">
            {t('dedicatedServer.hostingOptions')}
          </h2>
          <p className="text-gray-400 text-center max-w-2xl mx-auto mb-12">
            {t('dedicatedServer.hostingOptionsDesc')}
          </p>

          <div className="grid md:grid-cols-2 gap-8">
            {hostingOptions.map((option, idx) => (
              <div
                key={idx}
                className={`relative bg-brand-darker border rounded-2xl p-8 ${
                  option.highlight
                    ? 'border-brand-green shadow-lg shadow-brand-green/10'
                    : 'border-gray-800'
                }`}
              >
                {option.highlight && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-brand-green text-black text-sm font-medium rounded-full">
                    {t('dedicatedServer.recommended')}
                  </div>
                )}

                <div className="flex items-center gap-4 mb-6">
                  <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${
                    option.highlight ? 'bg-brand-green/20' : 'bg-gray-800'
                  }`}>
                    <option.icon className={`w-7 h-7 ${option.highlight ? 'text-brand-green' : 'text-gray-400'}`} />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white">{option.title}</h3>
                    <div className="flex items-center gap-1 text-sm text-gray-400">
                      <MapPin className="w-3 h-3" />
                      {option.location}
                    </div>
                  </div>
                </div>

                <ul className="space-y-3">
                  {option.features.map((feature, fidx) => (
                    <li key={fidx} className="flex items-center gap-3 text-gray-300">
                      <CheckCircle className={`w-5 h-5 ${option.highlight ? 'text-brand-green' : 'text-gray-500'}`} />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* AI Section */}
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-500/10 border border-purple-500/30 rounded-full text-purple-400 text-sm mb-6">
            <Brain className="w-4 h-4" />
            Intelligence Artificielle Locale
          </div>
          <h2 className="text-3xl font-bold text-white mb-4">
            {t('dedicatedServer.aiTitle')}
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto">
            {t('dedicatedServer.aiDesc')}
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 mb-8">
          {aiModels.map((model, idx) => (
            <div
              key={idx}
              className={`relative bg-brand-darker border rounded-xl overflow-hidden ${
                model.recommended ? 'border-purple-500' : 'border-gray-800'
              }`}
            >
              {model.recommended && (
                <div className="absolute top-0 right-0 px-3 py-1 bg-purple-500 text-white text-xs font-medium">
                  {t('dedicatedServer.recommended')}
                </div>
              )}

              <div className={`h-2 bg-gradient-to-r ${model.color}`} />

              <div className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-10 h-10 rounded-lg bg-gradient-to-r ${model.color} flex items-center justify-center`}>
                    <Bot className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-white">{model.name}</h3>
                    <p className="text-xs text-gray-400">{model.params} {t('dedicatedServer.parameters')}</p>
                  </div>
                </div>

                <p className="text-gray-400 text-sm mb-4 break-words">{model.description}</p>

                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2 text-gray-300">
                    <Cpu className="w-4 h-4 text-gray-500" />
                    {model.specs}
                  </div>
                  <div className="flex items-center gap-2 text-gray-300">
                    <Sparkles className="w-4 h-4 text-gray-500" />
                    {model.useCase}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="bg-brand-darker border border-gray-800 rounded-xl p-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img
              src="https://ollama.com/public/ollama.png"
              alt="Ollama"
              className="w-12 h-12 rounded-lg"
              onError={(e) => { e.target.style.display = 'none' }}
            />
            <div>
              <div className="text-white font-medium">{t('dedicatedServer.poweredBy')}</div>
              <div className="text-sm text-gray-400">{t('dedicatedServer.localLLM')}</div>
            </div>
          </div>
          <a
            href="https://ollama.com/sylink/sylink:32b"
            target="_blank"
            rel="noopener noreferrer"
            className="px-4 py-2 bg-gray-800 text-gray-300 font-semibold rounded-lg hover:bg-gray-700 transition-colors flex items-center gap-2"
          >
            {t('dedicatedServer.viewOnOllama')}
            <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-gradient-to-r from-brand-green/10 via-brand-darker to-purple-500/10 py-16">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            {t('dedicatedServer.ctaTitle')}
          </h2>
          <p className="text-gray-400 mb-8">
            {t('dedicatedServer.ctaDesc')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/contact"
              className="px-8 py-3 bg-brand-green text-black font-semibold rounded-lg hover:bg-brand-green/90 transition-colors flex items-center justify-center gap-2"
            >
              {t('dedicatedServer.requestQuote')}
              <ArrowRight className="w-5 h-5" />
            </a>
            <button
              onClick={() => navigate('/pricing')}
              className="px-8 py-3 bg-brand-darker text-white font-semibold rounded-lg border border-gray-700 hover:border-gray-600 transition-colors"
            >
              {t('dedicatedServer.viewAllPlans')}
            </button>
          </div>
        </div>
      </div>

      {/* Trust Section */}
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid md:grid-cols-3 gap-8 text-center">
          <div>
            <div className="w-16 h-16 bg-blue-500/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Shield className="w-8 h-8 text-blue-400" />
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">{t('dedicatedServer.trustFrenchTech')}</h3>
            <p className="text-gray-400 text-sm break-words">
              {t('dedicatedServer.trustFrenchTechDesc')}
            </p>
          </div>
          <div>
            <div className="w-16 h-16 bg-green-500/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-green-400" />
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">{t('dedicatedServer.trustGDPR')}</h3>
            <p className="text-gray-400 text-sm break-words">
              {t('dedicatedServer.trustGDPRDesc')}
            </p>
          </div>
          <div>
            <div className="w-16 h-16 bg-purple-500/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Database className="w-8 h-8 text-purple-400" />
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">{t('dedicatedServer.trustProtectedData')}</h3>
            <p className="text-gray-400 text-sm break-words">
              {t('dedicatedServer.trustProtectedDataDesc')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DedicatedServer;
